// client/src/pages/Admin/ProjectManager/components/ProjectCard.tsx
/**
 * PROJECT CARD COMPONENT
 * 
 * Displays a single project in a card format with actions.
 * 
 * @module ProjectManager/Components/ProjectCard
 */

import React, { useState } from 'react';
import { MapPin, Star, Clock3, CalendarDays, Eye, Pencil, Trash2, MoreHorizontal, CheckCircle2, CircleDashed } from 'lucide-react';
import { Project } from '../types/project.types';
import { ProjectStatus as ProjectStatusEnum } from '../../../../shared/models/Project';
import { ProjectStatusBadge } from './ProjectStatusBadge';
import {
    formatCategoryLabel,
    formatImpactLabel,
    getCategoryEmoji,
    getImpactIcon,
    truncateText,
    formatDate,
    getStatusColor
} from '../utils/projectHelpers';
import { STATUS_OPTIONS, STATUS_CONFIG } from '../constants/project.constants';

interface ProjectCardProps {
    project: Project;
    isSelected: boolean;
    onToggleSelection: (id: string) => void;
    onEdit: (project: Project) => void;
    onDelete: (id: string) => void;
    onStatusChange: (id: string, status: ProjectStatusEnum) => void;
    onToggleActionMenu: (id: string, e: React.MouseEvent) => void;
    isActionMenuOpen: boolean;
    matchFields: string[];
    debouncedSearchTerm: string;
}

export const ProjectCard: React.FC<ProjectCardProps> = ({
    project,
    isSelected,
    onToggleSelection,
    onEdit,
    onDelete,
    onStatusChange,
    onToggleActionMenu,
    isActionMenuOpen,
    matchFields,
    debouncedSearchTerm
}) => {
    const [showFullDescription, setShowFullDescription] = useState(false);

    const categoryLabel = formatCategoryLabel(project.category);
    const CategoryIcon = getCategoryEmoji(project.category);
    const impactLabel = formatImpactLabel(project.impact);
    const ImpactIcon = getImpactIcon(project.impact);
    const isOngoing = project.isOngoing || 
        (project.endDate && new Date(project.endDate) > new Date());

    // Get match fields safely
    const safeMatchFields = Array.isArray(matchFields) ? matchFields : [];
    const hasMatches = safeMatchFields.length > 0 && debouncedSearchTerm.length > 0;

    return (
        <div 
            className={`project-item ${isSelected ? 'selected' : ''}`}
            role="article"
            aria-label={`Project: ${project.title}`}
        >
            <div className="checkbox-wrapper">
                <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onToggleSelection(project._id!)}
                    aria-label={`Select ${project.title}`}
                    className="project-checkbox"
                />
            </div>

            <div className="project-info">
                <div className="project-title-row">
                    <h3>{project.title}</h3>
                    {project.featured && (
                        <span className="featured-badge" aria-label="Featured project">
                            <Star size={12} />
                            Featured
                        </span>
                    )}
                    {isOngoing && (
                        <span className="ongoing-badge" aria-label="Ongoing project">
                            <Clock3 size={12} />
                            Ongoing
                        </span>
                    )}
                </div>

                <div className="project-meta">
                    <ProjectStatusBadge status={project.status} />
                    <span className="category-badge">
                        <CategoryIcon size={12} />
                        {categoryLabel}
                    </span>
                    <span className={`impact-badge ${project.impact}`}>
                        <ImpactIcon size={12} />
                        {impactLabel}
                    </span>
                    {project.regions && project.regions.length > 0 && (
                        <span className="region-badge">
                            <MapPin size={12} />
                            {project.regions.slice(0, 2).join(', ')}
                            {project.regions.length > 2 && ` +${project.regions.length - 2}`}
                        </span>
                    )}
                </div>

                <p className="project-description">
                    {showFullDescription 
                        ? project.description 
                        : truncateText(project.description, 200)
                    }
                    {project.description.length > 200 && (
                        <button
                            type="button"
                            className="read-more-btn"
                            onClick={() => setShowFullDescription(!showFullDescription)}
                            aria-label={showFullDescription ? 'Show less' : 'Show more'}
                        >
                            {showFullDescription ? ' Show less' : ' Show more'}
                        </button>
                    )}
                </p>

                {hasMatches && safeMatchFields.length > 0 && (
                    <div className="match-indicators">
                        <span className="match-label">Matches in:</span>
                        {safeMatchFields.map((field, index) => (
                            <span key={index} className="match-tag">
                                {field === 'title' && 'Title'}
                                {field === 'subtitle' && 'Subtitle'}
                                {field === 'description' && 'Description'}
                                {field === 'fullDescription' && 'Full Description'}
                                {field === 'tags' && 'Tags'}
                                {field === 'category' && 'Category'}
                                {field === 'regions' && 'Regions'}
                                {field === 'districts' && 'Districts'}
                            </span>
                        ))}
                    </div>
                )}

                <div className="project-stats">
                    {project.startDate && (
                        <span className="stat-item">
                            <CalendarDays size={14} />
                            {formatDate(project.startDate)}
                            {project.endDate && ` → ${formatDate(project.endDate)}`}
                        </span>
                    )}
                    {project.views !== undefined && (
                        <span className="stat-item">
                            <Eye size={14} />
                            {project.views}
                        </span>
                    )}
                    {project.updatedAt && (
                        <span className="stat-item">
                            Updated {formatDate(project.updatedAt)}
                        </span>
                    )}
                </div>
            </div>

            <div className="project-actions">
                <select
                    value={project.status}
                    onChange={(e) => onStatusChange(project._id!, e.target.value as ProjectStatusEnum)}
                    className="status-select"
                    aria-label={`Change status for ${project.title}`}
                >
                    {STATUS_OPTIONS.map((status) => (
                        <option key={status} value={status}>
                            {STATUS_CONFIG[status].label}
                        </option>
                    ))}
                </select>

                <div className="action-buttons">
                    <button
                        className="action-btn action-btn-edit"
                        onClick={() => onEdit(project)}
                        aria-label={`Edit ${project.title}`}
                    >
                        <Pencil size={14} />
                        Edit
                    </button>

                    <div className="action-menu-wrapper">
                        <button
                            className="action-btn action-btn-menu"
                            onClick={(e) => onToggleActionMenu(project._id!, e)}
                            aria-label="More actions"
                            aria-expanded={isActionMenuOpen}
                        >
                            <MoreHorizontal size={16} />
                        </button>

                        {isActionMenuOpen && (
                            <div className="action-menu-dropdown" role="menu">
                                <button
                                    className="action-menu-item"
                                    onClick={() => onEdit(project)}
                                    role="menuitem"
                                >
                                    <Pencil size={14} />
                                    Edit Project
                                </button>
                                <button
                                    className="action-menu-item"
                                    onClick={() => {
                                        const newStatus = project.status === ProjectStatusEnum.PUBLISHED
                                            ? ProjectStatusEnum.DRAFT
                                            : ProjectStatusEnum.PUBLISHED;
                                        onStatusChange(project._id!, newStatus);
                                    }}
                                    role="menuitem"
                                >
                                    {project.status === ProjectStatusEnum.PUBLISHED ? <CircleDashed size={14} /> : <CheckCircle2 size={14} />}
                                    {project.status === ProjectStatusEnum.PUBLISHED ? 'Unpublish' : 'Publish'}
                                </button>
                                <button
                                    className="action-menu-item danger"
                                    onClick={() => onDelete(project._id!)}
                                    role="menuitem"
                                >
                                    <Trash2 size={14} />
                                    Delete Project
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};